import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { normalizeLanguage } from "../i18n";
import {setRuntimeLanguage,currentLanguage} from "../i18n/runtime";
import { api, API } from "./api";

const TOKEN_KEY = "praxis_admin_token";
const IDLE_LOGOUT_ENABLED = false;

function readToken() {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) : null;
  } catch {
    return null;
  }
}

function writeToken(t) {
  try {
    if (t) window.localStorage.setItem(TOKEN_KEY, t);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// Attach bearer token to every axios request on our instance.
api.interceptors.request.use((config) => {
  const t = readToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!readToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      writeToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    // Direct axios call (not `api`) so the interceptor doesn't send a stale token.
    const identifier = email.trim().toLowerCase();
    const presentationNames = ["presentation", "evi", "sylwia", "nina"];
    const loginEmail = presentationNames.includes(identifier) ? `${identifier}@essence-atlas.com` : identifier;
    const { data } = await axios.post(`${API}/auth/login`, { email: loginEmail, password });
    writeToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(()=>{});
    writeToken(null);
    setUser(null);
  }, []);

  const acceptSession = useCallback(async (token) => {
    writeToken(token);
    await checkAuth();
  }, [checkAuth]);

  // Single source of truth for the content language: the account field. Writes
  // PATCH /auth/me and updates local state in place (no reload). Used by both
  // the UserMenu setting and the admin header switch.
  const setContentLanguage = useCallback(async (lang) => {
    const next = normalizeLanguage(lang);
    const { data } = await api.patch("/auth/me", { content_language: next });
    setUser((u) => (u ? { ...u, content_language: data.content_language } : u));
    return data.content_language;
  }, []);

  // ───────────────────────────────────────────────────────────────
  // Auto-logout after IDLE_TIMEOUT_MS of no user interaction (DSGVO TOM T6).
  // Only active when a user is logged in. Resets on common UI events.
  //
  // TEMPORARILY DISABLED (single-user phase): the idle-logout interrupts live
  // case-taking. Re-enable for GDPR once real patient data / multi-practitioner
  // use begins by flipping IDLE_LOGOUT_ENABLED back to true. The logic below is
  // kept intact but inert.
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!IDLE_LOGOUT_ENABLED) return undefined;
    if (!user) return undefined;
    const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Silent logout — user will land on the login page on next protected nav.
        writeToken(null);
        setUser(null);
      }, IDLE_TIMEOUT_MS);
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  setRuntimeLanguage(user?.content_language || (typeof window!=='undefined' ? new URLSearchParams(window.location.search).get('lang') : 'en'));
  const displayLanguage=currentLanguage();
  useEffect(()=>{document.documentElement.lang=displayLanguage;},[displayLanguage]);
  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: checkAuth, setContentLanguage, acceptSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
